import { useState, useRef, useEffect, type KeyboardEvent } from 'react';
import { Input, Select, SelectItem } from '@heroui/react';

export interface SelectOption {
  value: string;
  label: string;
}

interface InlineEditBaseProps {
  value: string | number | null | undefined;
  onSave: (value: string) => void;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  disabled?: boolean;
}

interface InlineEditText extends InlineEditBaseProps {
  type?: 'text';
}

interface InlineEditNumber extends InlineEditBaseProps {
  type: 'number';
  min?: number;
  max?: number;
  step?: number;
}

interface InlineEditSelect extends InlineEditBaseProps {
  type: 'select';
  options: SelectOption[];
}

export type InlineEditProps = InlineEditText | InlineEditNumber | InlineEditSelect;

export default function InlineEdit(props: InlineEditProps) {
  const { value, onSave, placeholder = '—', className = '', disabled = false } = props;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const displayValue =
    value !== null && value !== undefined && value !== '' ? String(value) : null;

  function startEdit() {
    if (disabled) return;
    setDraft(displayValue ?? '');
    setEditing(true);
  }

  function confirm() {
    setEditing(false);
    if (draft !== String(value ?? '')) {
      onSave(draft);
    }
  }

  function cancel() {
    setEditing(false);
    setDraft('');
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      confirm();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancel();
    }
  }

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  if (editing && props.type === 'select') {
    return (
      <Select
        size="sm"
        selectedKeys={draft ? new Set([draft]) : new Set()}
        onSelectionChange={(keys) => {
          const selected = Array.from(keys)[0] as string;
          setDraft(selected ?? '');
        }}
        onBlur={confirm}
        autoFocus
        classNames={{
          base: 'min-w-0',
          trigger:
            'h-7 min-h-7 bg-[var(--bg-hover)] border border-[var(--accent)] rounded text-xs data-[focus=true]:border-[var(--accent)]',
          value: 'text-xs text-[var(--text-primary)]',
          popoverContent: 'bg-[var(--bg-panel)] border border-[var(--border)]',
        }}
        aria-label="edit value"
      >
        {(props as InlineEditSelect).options.map((opt) => (
          <SelectItem
            key={opt.value}
            classNames={{ base: 'text-[var(--text-primary)] data-[hover=true]:bg-[var(--bg-hover)]' }}
          >
            {opt.label}
          </SelectItem>
        ))}
      </Select>
    );
  }

  if (editing) {
    return (
      <Input
        ref={inputRef}
        size="sm"
        type={props.type === 'number' ? 'number' : 'text'}
        value={draft}
        onValueChange={setDraft}
        onBlur={confirm}
        onKeyDown={handleKeyDown}
        min={props.type === 'number' ? (props as InlineEditNumber).min : undefined}
        max={props.type === 'number' ? (props as InlineEditNumber).max : undefined}
        step={props.type === 'number' ? (props as InlineEditNumber).step : undefined}
        classNames={{
          base: 'min-w-0',
          inputWrapper:
            'h-7 min-h-7 bg-[var(--bg-hover)] border border-[var(--accent)] rounded shadow-none data-[focus=true]:border-[var(--accent)] data-[hover=true]:bg-[var(--bg-hover)]',
          input: `text-xs text-[var(--text-primary)] ${props.type === 'number' ? 'font-ui' : ''}`,
        }}
        aria-label="edit value"
      />
    );
  }

  return (
    <span
      onClick={startEdit}
      title={disabled ? undefined : 'Click to edit'}
      className={[
        'inline-block min-w-[2rem] cursor-text text-xs leading-5 rounded px-0.5 transition-colors',
        !disabled &&
          'hover:border-b hover:border-dashed hover:border-[var(--border-em)] hover:text-[var(--text-primary)]',
        disabled && 'cursor-default opacity-60',
        displayValue ? 'text-[var(--text-primary)]' : 'text-[var(--text-muted)] italic',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {displayValue ?? placeholder}
    </span>
  );
}
